import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { 
  Table, 
  Button, 
  Input, 
  Space, 
  Popconfirm, 
  Modal, 
  Form, 
  DatePicker, 
  Switch,
  Select,
  Card,
  Typography,
  Row,
  Col
} from 'antd';
import styles from './MentorPanel.module.css';
import { 
  PlusOutlined, 
  EditOutlined, 
  DeleteOutlined, 
  SearchOutlined,
  BookOutlined
} from '@ant-design/icons';
import { useToast } from '../../../contexts/ToastContext';
import { apiService } from '../../../services/api';
import { MentorEducationResponse, CreateMentorEducationRequest, UpdateMentorEducationRequest } from '../../../types';
import { mentorProfileQueryKeys } from '../../../lib/queryClient';
import PanelTabCard from '../../../components/Common/PanelTabCard';
import TabListLoading from '../../../components/Common/TabListLoading';
import dayjs from 'dayjs';

const { Title } = Typography;
const { Search } = Input;
const { Option } = Select;

const EducationTab: React.FC = () => {
  const toast = useToast();
  const queryClient = useQueryClient();
  const [searchText, setSearchText] = useState('');
  const [modalVisible, setModalVisible] = useState(false);
  const [editingEducation, setEditingEducation] = useState<MentorEducationResponse | null>(null);
  const [form] = Form.useForm();

  const { data: educations = [], isLoading: loading } = useQuery({
    queryKey: mentorProfileQueryKeys.educations,
    queryFn: async () => {
      const response = await apiService.getMentorEducations();
      if (!response.success) throw new Error(response.message);
      return response.data ?? [];
    },
    staleTime: 1000 * 60 * 5,
  });

  const createMutation = useMutation({
    mutationFn: async (data: CreateMentorEducationRequest) => {
      const response = await apiService.createMentorEducation(data);
      if (!response.success) throw new Error(response.message);
      return response.data;
    },
    onSuccess: () => {
      toast.success('Eğitim bilgisi başarıyla eklendi.');
      setModalVisible(false);
      queryClient.invalidateQueries({ queryKey: mentorProfileQueryKeys.educations });
    },
    onError: (err) => toast.error(err instanceof Error ? err.message : 'Hata oluştu.')
  });

  const updateMutation = useMutation({
    mutationFn: async ({ id, data }: { id: number; data: UpdateMentorEducationRequest }) => {
      const response = await apiService.updateMentorEducation(id, data);
      if (!response.success) throw new Error(response.message);
      return response.data;
    },
    onSuccess: () => {
      toast.success('Eğitim bilgisi başarıyla güncellendi.');
      setModalVisible(false);
      queryClient.invalidateQueries({ queryKey: mentorProfileQueryKeys.educations });
    },
    onError: (err) => toast.error(err instanceof Error ? err.message : 'Hata oluştu.')
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: number) => {
      const response = await apiService.deleteMentorEducationById(id);
      if (!response.success) throw new Error(response.message);
      return response;
    },
    onSuccess: () => {
      toast.success('Eğitim bilgisi başarıyla silindi.');
      queryClient.invalidateQueries({ queryKey: mentorProfileQueryKeys.educations });
    },
    onError: (err) => toast.error(err instanceof Error ? err.message : 'Hata oluştu.')
  });

  const handleAdd = () => {
    setEditingEducation(null);
    form.resetFields();
    setModalVisible(true);
  };

  const handleEdit = (education: MentorEducationResponse) => {
    setEditingEducation(education);
    form.setFieldsValue({
      schoolName: education.schoolName,
      department: education.department,
      degree: education.degree,
      startDate: education.startDate ? dayjs(education.startDate) : null,
      endDate: education.endDate ? dayjs(education.endDate) : null,
      isGraduated: education.isGraduated,
      description: education.description
    });
    setModalVisible(true);
  };

  const handleDelete = (id: number) => {
    deleteMutation.mutate(id);
  };

  const handleModalOk = async () => {
    try {
      const values = await form.validateFields();
      const educationData = {
        schoolName: values.schoolName,
        department: values.department,
        degree: values.degree,
        startDate: values.startDate.format('YYYY-MM-DD'),
        endDate: values.endDate ? values.endDate.format('YYYY-MM-DD') : undefined,
        isGraduated: values.isGraduated || false,
        description: values.description
      };

      if (editingEducation) {
        updateMutation.mutate({ id: editingEducation.id, data: educationData });
      } else {
        createMutation.mutate(educationData);
      }
    } catch {
      // Form validation failed
    }
  };

  const handleModalCancel = () => {
    setModalVisible(false);
    setEditingEducation(null);
    form.resetFields();
  };

  const filteredEducations = educations.filter(education =>
    education.schoolName.toLowerCase().includes(searchText.toLowerCase()) ||
    education.department.toLowerCase().includes(searchText.toLowerCase()) ||
    (education.degree && education.degree.toLowerCase().includes(searchText.toLowerCase()))
  );

  const columns = [
    {
      title: 'Okul Adı',
      dataIndex: 'schoolName',
      key: 'schoolName',
      sorter: (a: MentorEducationResponse, b: MentorEducationResponse) => 
        a.schoolName.localeCompare(b.schoolName),
    },
    {
      title: 'Bölüm',
      dataIndex: 'department',
      key: 'department',
      sorter: (a: MentorEducationResponse, b: MentorEducationResponse) => 
        a.department.localeCompare(b.department),
    },
    {
      title: 'Derece',
      dataIndex: 'degree',
      key: 'degree',
      render: (degree: string) => degree || '-',
    },
    {
      title: 'Başlangıç Tarihi',
      dataIndex: 'startDate',
      key: 'startDate',
      render: (date: string) => dayjs(date).format('DD.MM.YYYY'),
      sorter: (a: MentorEducationResponse, b: MentorEducationResponse) => 
        new Date(a.startDate).getTime() - new Date(b.startDate).getTime(),
    },
    {
      title: 'Bitiş Tarihi',
      dataIndex: 'endDate',
      key: 'endDate',
      render: (date: string) => date ? dayjs(date).format('DD.MM.YYYY') : '-',
      sorter: (a: MentorEducationResponse, b: MentorEducationResponse) => {
        if (!a.endDate && !b.endDate) return 0;
        if (!a.endDate) return 1;
        if (!b.endDate) return -1;
        return new Date(a.endDate).getTime() - new Date(b.endDate).getTime();
      },
    },
    {
      title: 'Mezun',
      dataIndex: 'isGraduated',
      key: 'isGraduated',
      render: (isGraduated: boolean) => isGraduated ? 'Evet' : 'Hayır',
      filters: [
        { text: 'Evet', value: true },
        { text: 'Hayır', value: false },
      ],
      onFilter: (value: boolean | React.Key, record: MentorEducationResponse) => record.isGraduated === value,
    },
    {
      title: 'İşlemler',
      key: 'actions',
      render: (_: any, record: MentorEducationResponse) => (
        <Space>
          <Button
            type="primary"
            icon={<EditOutlined />}
            size="small"
            onClick={() => handleEdit(record)}
            style={{
              backgroundColor: 'var(--text-secondary)',
              borderColor: 'var(--text-secondary)',
              color: 'white',
              fontWeight: 500
            }}
          >
            Düzenle
          </Button>
          <Popconfirm
            title="Eğitim bilgisini silmek istediğinizden emin misiniz?"
            onConfirm={() => handleDelete(record.id)}
            okText="Evet"
            cancelText="Hayır"
            okButtonProps={{
              style: {
                backgroundColor: '#ef4444',
                borderColor: '#ef4444',
                color: 'white',
                fontWeight: 500
              }
            }}
            cancelButtonProps={{
              style: {
                backgroundColor: 'white',
                borderColor: '#d1d5db',
                color: 'var(--text-secondary)',
                fontWeight: 500
              }
            }}
          >
            <Button
              type="primary"
              danger
              icon={<DeleteOutlined />}
              size="small"
              style={{
                backgroundColor: '#ef4444',
                borderColor: '#ef4444',
                color: 'white',
                fontWeight: 500
              }}
            >
              Sil
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <>
    <PanelTabCard
      title="Eğitim Bilgileri"
      description="Okul, bölüm ve derece bilgilerinizi ekleyin veya düzenleyin."
    >
      <style>
        {`
          .ant-btn:not(.ant-btn-primary):hover {
            background-color: inherit !important;
            border-color: inherit !important;
            color: inherit !important;
            transform: none !important;
            box-shadow: none !important;
          }
          .ant-btn:not(.ant-btn-primary):focus {
            background-color: inherit !important;
            border-color: inherit !important;
            color: inherit !important;
            transform: none !important;
            box-shadow: none !important;
          }
          .ant-btn:not(.ant-btn-primary):active {
            background-color: inherit !important;
            border-color: inherit !important;
            color: inherit !important;
            transform: none !important;
            box-shadow: none !important;
          }
        `}
      </style>
        <TabListLoading loading={loading}>
        <Row justify="space-between" align="middle" style={{ marginBottom: 16 }}>
          <Col>
          </Col>
          <Col>
            <Space>
              <Search
                placeholder="Okul, bölüm veya derece ara..."
                allowClear
                style={{ width: 300 }}
                onChange={(e) => setSearchText(e.target.value)}
                prefix={<SearchOutlined />}
              />
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={handleAdd}
                style={{
                  backgroundColor: '#8b949e',
                  borderColor: '#8b949e',
                  color: 'white',
                  fontWeight: 500
                }}
              >
                Eğitim Ekle
              </Button>
            </Space>
          </Col>
        </Row>

        {/* eslint-disable react/jsx-no-duplicate-props */}
        <Table
          scroll={{ x: 'max-content' }}
          columns={columns}
          dataSource={filteredEducations}
          rowKey="id"
          loading={loading}
          pagination={{
            pageSize: 10,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total, range) => 
              `${range[0]}-${range[1]} / ${total} eğitim bilgisi`,
          }}
        />
        {/* eslint-enable react/jsx-no-duplicate-props */}
        </TabListLoading>

      <Modal
        title={editingEducation ? 'Eğitim Bilgisi Düzenle' : 'Yeni Eğitim Bilgisi'}
        open={modalVisible}
        onOk={handleModalOk}
        onCancel={handleModalCancel}
        width={600}
        okText={editingEducation ? 'Güncelle' : 'Ekle'}
        cancelText="İptal"
        okButtonProps={{
          style: {
            backgroundColor: 'var(--text-secondary)',
            borderColor: 'var(--text-secondary)',
            color: 'white',
            fontWeight: 500,
            height: '32px',
            minWidth: '80px',
            filter: 'none'
          }
        }}
        cancelButtonProps={{
          style: {
            backgroundColor: 'white',
            borderColor: '#d1d5db',
            color: 'var(--text-secondary)',
            fontWeight: 500,
            height: '32px',
            minWidth: '80px',
            filter: 'none'
          }
        }}
      >
        <Form
          form={form}
          layout="vertical"
          initialValues={{
            isGraduated: false
          }}
        >
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="schoolName"
                label="Okul Adı"
                rules={[{ required: true, message: 'Okul adı gereklidir' }]}
              >
                <Input placeholder="Okul adını girin" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="department"
                label="Bölüm"
                rules={[{ required: true, message: 'Bölüm gereklidir' }]}
              >
                <Input placeholder="Bölüm adını girin" />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="degree"
                label="Derece"
              >
                <Select placeholder="Derece seçin" allowClear>
                  <Option value="Lisans">Lisans</Option>
                  <Option value="Yüksek Lisans">Yüksek Lisans</Option>
                  <Option value="Doktora">Doktora</Option>
                  <Option value="Ön Lisans">Ön Lisans</Option>
                  <Option value="Diğer">Diğer</Option>
                </Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="isGraduated"
                label="Mezun Durumu"
                valuePropName="checked"
                dependencies={['startDate']}
                rules={[
                  {
                    validator: (_, value) => {
                      if (!value) return Promise.resolve();
                      const startDate = form.getFieldValue('startDate');
                      if (startDate && dayjs(startDate).isAfter(dayjs(), 'day')) {
                        return Promise.reject(new Error('Gelecekteki bir tarih için mezun durumu seçilemez'));
                      }
                      return Promise.resolve();
                    }
                  }
                ]}
              >
                <Switch checkedChildren="Mezun" unCheckedChildren="Devam Ediyor" />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="startDate"
                label="Başlangıç Tarihi"
                rules={[
                  { required: true, message: 'Başlangıç tarihi gereklidir' },
                  {
                    validator: (_, value) => {
                      if (!value) return Promise.resolve();
                      const today = dayjs().startOf('day');
                      const selectedDate = dayjs(value).startOf('day');
                      if (selectedDate.isAfter(today)) {
                        return Promise.reject(new Error('Başlangıç tarihi bugünden sonra olamaz'));
                      }
                      const endDate = form.getFieldValue('endDate');
                      if (endDate && selectedDate.isAfter(dayjs(endDate))) {
                        return Promise.reject(new Error('Başlangıç tarihi bitiş tarihinden sonra olamaz'));
                      }
                      return Promise.resolve();
                    }
                  }
                ]}
              >
                <DatePicker 
                  style={{ width: '100%' }} 
                  disabledDate={(current) => current && current.isAfter(dayjs(), 'day')}
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="endDate"
                label="Bitiş Tarihi"
                dependencies={['startDate']}
                rules={[
                  {
                    validator: (_, value) => {
                      if (!value) return Promise.resolve();
                      const startDate = form.getFieldValue('startDate');
                      if (startDate && dayjs(value).isBefore(dayjs(startDate))) {
                        return Promise.reject(new Error('Bitiş tarihi başlangıç tarihinden önce olamaz'));
                      }
                      return Promise.resolve();
                    }
                  }
                ]}
              >
                <DatePicker 
                  style={{ width: '100%' }} 
                  disabledDate={(current) => {
                    const startDate = form.getFieldValue('startDate');
                    if (startDate && current) {
                      return current.isBefore(dayjs(startDate), 'day');
                    }
                    return false;
                  }}
                />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item
            name="description"
            label="Açıklama"
          >
            <Input.TextArea 
              rows={3} 
              placeholder="Eğitim hakkında açıklama yazın (isteğe bağlı)"
            />
          </Form.Item>
        </Form>
      </Modal>
    </PanelTabCard>
    </>
  );
};

export default EducationTab;
